import React, { useState, useEffect } from 'react';
import { FaEye, FaEdit, FaTrashAlt, FaPlus, FaUpload, FaTimes, FaImage } from 'react-icons/fa';
import GenericTable from "../../../DynamicComp/Table";
import Button from '../../../DynamicComp/Button';
import Input from '../../../DynamicComp/Input';
import axiosClient, { deleteData, getData } from '../../../../global/Globalapi';
import { showAlert } from '../../../DynamicComp/Alert'; // Already imported

const Productmasterimg = ({ productId, onImagesUpdate, initialImages }) => {
    const [editId, setEditId] = useState(null);
    const [images, setImages] = useState([]);
    const [workingImages, setWorkingImages] = useState([]);
    const [newImage, setNewImage] = useState({
        file: null,
        displayOrder: '',
        description: '',
        file_name: ''
    });
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const baseURL = axiosClient.defaults.baseURL

    useEffect(() => {
        if (initialImages && initialImages.length > 0) {
            const formattedImages = initialImages.map(img => ({
                id: img.id || img.image_id,
                file: img.file || (img.file_name ? `${baseURL}/uploads/${img.file_name}` : null),
                file_name: img.file_name,
                displayOrder: img.displayOrder || img.display_order || 0,
                description: img.description || '',
                isUpdated: false
            }));
            setImages(formattedImages);
            setWorkingImages(formattedImages);
        } else if (productId) {
            fetchImages();
        } else {
            setImages([]);
            setWorkingImages([]);
        }
    }, [productId, initialImages, baseURL]); // Added dependency array for safety

    useEffect(() => {
        return () => {
            if (selectedImage instanceof File) {
                URL.revokeObjectURL(selectedImage);
            }
        };
    }, [selectedImage]);

    const fetchImages = async () => {
        try {
            const response = await getData(`/product_images/${productId}`);
            const formattedData = response.data.map((img) => ({
                id: img.image_id,
                file: `${baseURL}/uploads/${img.file_name}`,
                file_name: img.file_name,
                displayOrder: img.display_order,
                description: img.description,
                isUpdated: false
            }));
            setImages(formattedData);
            setWorkingImages([...formattedData]);
        } catch (err) {
            console.error('Failed to fetch images:', err);
            // Optionally alert user about fetch failure
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setNewImage({
            ...newImage,
            file: file,
            file_name: file?.name || ''
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewImage(prev => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setNewImage({ file: null, displayOrder: '', description: '', file_name: '' });
        setEditId(null);
        setShowUploadForm(false);
    };

    /**
     * REVISED: Replaced native alert with showAlert for validation and success.
     */
    const handleSaveImage = () => {
        if (!newImage.file && !editId) {
            showAlert({ 
                type: 'warning',
                title: 'Missing File',
                message: 'Please select an image file before saving.' 
            });
            return;
        }

        const updatedImage = {
            id: editId || Date.now() ,
            file: newImage.file,
            file_name: newImage.file instanceof File ? newImage.file.name : newImage.file_name,
            displayOrder: parseInt(newImage.displayOrder) || 0,
            description: newImage.description || '',
            isUpdated: true,
            isNew: !editId // Mark as new if not editing
        };
        console.log("update image id sis",updatedImage.id)
        
        let updatedImages;
        if (editId) {
            updatedImages = workingImages.map(img =>
                img.id === editId ? updatedImage : img
            );
        } else {
            updatedImages = [...workingImages, updatedImage];
        }

        setWorkingImages(updatedImages);
        if (onImagesUpdate) {
            // Pass the necessary data back to the parent component
            onImagesUpdate(updatedImages.map(img => ({
                id: img.id,
                file: img.file,
                file_name: img.file_name,
                displayOrder: img.displayOrder,
                description: img.description,
                isUpdated: img.isUpdated,
                isNew: img.isNew || false // Ensure isNew flag is carried
            })));
        }
        
        showAlert({ 
            type: 'success', 
            title: 'Saved!', 
            message: `Image ${editId ? 'updated' : 'added'} successfully.` 
        });

        resetForm();
    };

    /**
     * REVISED: Replaced window.confirm and native alert with showAlert for confirmation and feedback.
     */
    const handleDelete = (id, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        const imageToDelete = workingImages.find(img => img.id === id);
        if (!imageToDelete) return;

        // Define the function that runs on SweetAlert confirmation
        const performDeletion = async () => {
            if (imageToDelete.isNew) {
                // Case 1: Delete a newly added, unsaved image (local state only)
                const updatedImages = workingImages.filter(img => img.id !== id);
                setWorkingImages(updatedImages);
                if (onImagesUpdate) onImagesUpdate(updatedImages);
                
                showAlert({
                    type: 'success',
                    title: 'Removed!',
                    message: 'New image removed from the list.',
                });
                return; 
            }

            try {
                // Case 2: Delete an existing image (call API)
                await deleteData(`/product_images/${id}`);
                
                const updatedImages = workingImages.filter(img => img.id !== id);
                setWorkingImages(updatedImages);
                if (onImagesUpdate) onImagesUpdate(updatedImages);
                if (editId === id) resetForm();

                showAlert({
                    type: 'success',
                    title: 'Deleted!',
                    message: 'Image deleted successfully from the server.',
                });

            } catch (error) {
                console.error('Delete failed:', error);
                const serverMessage = error.response?.data?.message || 'The server returned an error.';
                
                showAlert({
                    type: 'error',
                    title: 'Failed!',
                    message: `Failed to delete image on server: ${serverMessage}`,
                });
            }
        };

        // Display SweetAlert confirmation dialog
        showAlert({
            type: 'warning',
            title: 'Are you sure?',
            message: 'You want to delete this image? This action cannot be undone.',
            buttons: {
                cancel: "No, cancel!",
                confirm: {
                    text: "Yes, delete it!",
                    value: true,
                    visible: true,
                    closeModal: true
                }
            },
            dangerMode: true,
        }, performDeletion); // Pass the action as the callback
    };

    const handleEdit = (id, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        const imageToEdit = workingImages.find(img => img.id === id);
        console.log('Editing image:', imageToEdit);
        if (imageToEdit) {
            setNewImage({
                file: null,
                displayOrder: imageToEdit.displayOrder.toString(),
                description: imageToEdit.description || '',
                file_name: imageToEdit.file_name || ''
            });
            setEditId(id);
            setShowUploadForm(true);
        }
    };

    const handleViewImage = (file, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (file) {
            setSelectedImage(file);
            setIsModalOpen(true);
        }
    };

    const columnimage = [
        { key: 'index', label: 'NO', sortable: false },
        { key: 'view', label: 'View', sortable: false },
        { key: 'order', label: 'Order', sortable: false },
        { key: 'description', label: 'Description', sortable: true },
        { key: 'actions', label: 'Actions', sortable: false }
    ];

    const tableData = workingImages.map((image, index) => ({
        ...image,
        index: index + 1,
        view: image.file_name || 'New Image',
        order: image.displayOrder,
        description: image.description || '',
        actions: image.id
    }));

    const customRenderers = {
        view: (row) => (
            <div className="flex justify-center">
                <button
                    type="button"
                    onClick={(e) => handleViewImage(row.file, e)}
                    className="text-blue-500 hover:text-blue-700"
                    title="View Image"
                    disabled={!row.file}
                >
                    <FaImage size={18} />
                </button>
            </div>
        ),
        actions: (row) => (
            <div className="flex gap-2 justify-center">
                <button
                    className="bg-blue-500 text-white p-1 rounded"
                    onClick={(e) => handleEdit(row.id, e)}
                >
                    <FaEdit />
                </button>
                <button
                    className="bg-red-500 text-white p-1 rounded"
                    onClick={(e) => handleDelete(row.id, e)}
                >
                    <FaTrashAlt />
                </button>
            </div>
        ),
    };

    return (
        <div className="p-6 w-full">
            {/* Image Preview Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-4xl max-h-screen overflow-auto">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-lg font-semibold">Image Preview</h3>
                            <button 
                                onClick={() => setIsModalOpen(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <FaTimes size={20} />
                            </button>
                        </div>
                        <div className="p-4">
                            {selectedImage && (
                                <img 
                                    src={selectedImage instanceof File ? URL.createObjectURL(selectedImage) : selectedImage} 
                                    alt="Preview" 
                                    className="max-w-full max-h-[60vh] object-contain"
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold">Product Image List</h1>
                {!showUploadForm && (
                    <Button
                        variant="danger"
                        onClick={() => {
                            resetForm();
                            setShowUploadForm(true);
                        }}
                        className="flex items-center px-3 py-2"
                    >
                        <FaPlus className="mr-2" />
                        Add Image
                    </Button>
                )}
            </div>

            <div className="mb-8">
                <GenericTable
                    columns={columnimage}
                    data={tableData}
                    customRenderers={customRenderers}
                    rowKey="id"
                    stripedRows={true}
                    hoverEffect={true}
                    headerClassName="bg-red-700 text-white"
                    emptyMessage="No images added yet"
                />
            </div>

            {showUploadForm && (
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <h2 className="text-xl font-semibold mb-4">
                        {editId !== null ? 'Update Image' : 'Upload New Image'}
                    </h2>

                    <div className="grid grid-cols-1 gap-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Input
                                type="file"
                                label="Image File"
                                name="file"
                                onChange={handleFileChange}
                                accept="image/*"
                                className="w-full"
                                required={!editId}
                                key={editId || 'new'}
                                helperText={editId ? `Current file: ${newImage.file_name || 'No file selected'}` : undefined}
                            />

                            <Input
                                type="number"
                                label="Display Order"
                                name="displayOrder"
                                value={newImage.displayOrder || ''}
                                onChange={handleInputChange}
                                placeholder="Enter display order"
                                className="w-full"
                                min="0"
                            />
                        </div>

                        <div>
                            <Input 
                                type="text"
                                label="Description"
                                name="description"
                                value={newImage.description}
                                onChange={handleInputChange}
                                placeholder="Enter image description"
                                className="w-full"
                            />
                        </div>
                    </div>

                    <div className="flex justify-center space-x-6 mt-6 p-1 mr-3">
                        <Button
                            variant="secondary"
                            onClick={resetForm}
                            className="flex items-center h-15 w-20 p-2"
                        >
                            <FaTimes className="" />
                            Cancel  
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleSaveImage}
                            className="flex items-center h-15 w-15 gap-2"
                            disabled={!newImage.file && !editId}
                        >
                            <FaUpload className="ml-0.5" />
                            {editId !== null ? 'Update Image' : 'Upload Img'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Productmasterimg;